import { useState } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import { useSettings } from "../../contexts/SettingsContext";
import { useSleeperConnection } from "../../hooks/useSleeperConnection";
import LeagueSelector from "../../components/LeagueSelector";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
//import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
//import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
//import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
//import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
//import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
//import PieChartOutlineOutlinedIcon from "@mui/icons-material/PieChartOutlineOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
//import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";

const Item = ({ title, to, icon, selected, setSelected, isCollapsed, onNavigate }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      onClick={() => { setSelected(title); onNavigate?.(); }}
      icon={icon}
    >
      {!isCollapsed && <Typography>{title}</Typography>}
      <Link to={to} />
    </MenuItem>
  );
};

const Sidebar = ({ owners = {}, onToggleCollapse, initialCollapsed = false, onNavigate, isMobile = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const { selectedLeagueId, getBestAvailableLeagueId, handleSelectedLeagueChange } = useSettings();
  const { leagues } = useSleeperConnection();
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const [selected, setSelected] = useState("Dashboard");

  // Enhanced function to get the best league ID based on context and preferences
  const getBestLeagueId = () => {
    // First priority: If we're on a league page, use that league ID
    const urlMatch = location.pathname.match(/^\/leagues\/([^/]+)/);
    if (urlMatch) {
      const urlLeagueId = urlMatch[1];
      // Auto-update the selected league when navigating to a league page
      if (urlLeagueId !== selectedLeagueId) {
        handleSelectedLeagueChange(urlLeagueId);
      }
      return urlLeagueId;
    }
    
    // Second priority: Use the smart fallback system with available leagues
    return getBestAvailableLeagueId(leagues);
  };

  const currentLeagueId = getBestLeagueId();

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    onToggleCollapse?.();
  };

  const effectiveIsCollapsed = isMobile ? false : isCollapsed;
  const handleHeaderClick = () => {
    if (isMobile) {
      // In mobile Drawer, clicking the header should close the Drawer entirely
      onNavigate?.();
      return;
    }
    handleToggleCollapse();
  };

  return (
    <Box
      sx={{
        height: "100vh",
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
          height: "100%", // Ensure the sidebar content also fills the height
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .pro-inner-item:hover": {
          color: "#868dfb !important",
        },
        "& .pro-menu-item.active": {
          color: "#6870fa !important",
        },
      }}
    >
      <ProSidebar collapsed={effectiveIsCollapsed}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={handleHeaderClick}
            icon={effectiveIsCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            {!effectiveIsCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Typography variant="h3" color={colors.grey[100]}>
                  WeezLeagues
                </Typography>
                <IconButton onClick={isMobile ? onNavigate : handleToggleCollapse}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {/* League Selector */}
          {!effectiveIsCollapsed && (
            <LeagueSelector isCollapsed={effectiveIsCollapsed} />
          )}

          {/*!isCollapsed && (
            <Box mb="25px">
              <Box display="flex" justifyContent="center" alignItems="center">
                <img
                  alt="profile-user"
                  width="100px"
                  height="100px"
                  src={`../../assets/user.png`}
                  style={{ cursor: "pointer", borderRadius: "50%" }}
                />
              </Box>
              <Box textAlign="center">
                <Typography
                  variant="h2"
                  color={colors.grey[100]}
                  fontWeight="bold"
                  sx={{ m: "10px 0 0 0" }}
                >
                  Irweezy
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[500]}>
                  Commissioner
                </Typography>
              </Box>
            </Box>
          )*/}

          <Box paddingLeft={effectiveIsCollapsed ? undefined : "10%"}>
            <Item
              title="Dashboard"
              to="/"
              icon={<HomeOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              isCollapsed={effectiveIsCollapsed}
              onNavigate={onNavigate}
            />

            {!effectiveIsCollapsed && (
              <Typography
                variant="h6"
                color={colors.grey[300]}
                sx={{ m: "15px 0px 5px 20px" }}
              >
                Keepers
              </Typography>
            )}
            <Item
              title="All Keepers"
              to={currentLeagueId ? `/leagues/${currentLeagueId}/keepers` : "/keepers"}
              icon={<PeopleOutlinedIcon />} // You can replace with a relevant icon for teams
              selected={selected}
              setSelected={setSelected}
              isCollapsed={effectiveIsCollapsed}
              onNavigate={onNavigate}
            />
            {Object.entries(owners).map(([id, name]) => (
              <Item
                key={id}
                title={name}
                to={`/owners/${name}`}
                icon={<PeopleOutlinedIcon />} // You can replace with a relevant icon for teams
                selected={selected}
                setSelected={setSelected}
                isCollapsed={effectiveIsCollapsed}
                onNavigate={onNavigate}
              />
            ))}
            {!effectiveIsCollapsed && (
              <Typography
                variant="h6"
                color={colors.grey[300]}
                sx={{ m: "15px 0 5px 20px" }}
              >
                Draftboard
              </Typography>
            )}
            <Item
              title="Draftboard"
              to="/draftboard"
              icon={<GridOnOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              isCollapsed={effectiveIsCollapsed}
              onNavigate={onNavigate}
            />

            {!effectiveIsCollapsed && (
              <Typography
                variant="h6"
                color={colors.grey[300]}
                sx={{ m: "15px 0px 5px 20px" }}
              >
                Players
              </Typography>
            )}
            <Item
              title="Players Table"
              to="/players"
              icon={<PersonOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              isCollapsed={effectiveIsCollapsed}
              onNavigate={onNavigate}
            />
            <Item
              title="Line Chart"
              to="/line"
              icon={<TimelineOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              isCollapsed={effectiveIsCollapsed}
              onNavigate={onNavigate}
            />
            {/* 
            <Item
              title="Calendar"
              to="/calendar"
              icon={<CalendarTodayOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="FAQ Page"
              to="/faq"
              icon={<HelpOutlineOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />

            <Typography
              variant="h6"
              color={colors.grey[300]}
              sx={{ m: "15px 0 5px 20px" }}
            >
              Charts
            </Typography>
            <Item
              title="Bar Chart"
              to="/bar"
              icon={<BarChartOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Pie Chart"
              to="/pie"
              icon={<PieChartOutlineOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Line Chart"
              to="/line"
              icon={<TimelineOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Geography Chart"
              to="/geography"
              icon={<MapOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            */}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
